import { useEffect, useRef, useState } from "hono/jsx";
import { Menu } from "./Menu.tsx";
import { HUD } from "./HUD.tsx";

interface PlaygroundRoomProps {
  roomId: string;
  user?: { id: string; isAnonymous?: boolean };
}

interface RoomInfo {
  found: boolean;
  isReserved?: boolean;
  isOwner?: boolean;
}

export function PlaygroundRoom({ roomId, user }: PlaygroundRoomProps) {
  const [connected, setConnected] = useState(false);
  const [roomNotFound, setRoomNotFound] = useState<
    boolean | { found: false; isReserved?: boolean; isOwner?: boolean }
  >(false);
  // deno-lint-ignore no-explicit-any
  const [initData, setInitData] = useState<any>(null);
  const [isHost, setIsHost] = useState(false);
  const [isPublic, setIsPublic] = useState(false);
  const [currentExperiment, setCurrentExperiment] = useState("chunks_culled");
  const [chunkDepth, setChunkDepth] = useState(1);
  const [chunkSize, setChunkSize] = useState(16);
  const [showGrid, setShowGrid] = useState(true);
  const [smartRender, setSmartRender] = useState(false);
  const [meshRender, setMeshRender] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [isDuplicate, setIsDuplicate] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // deno-lint-ignore no-explicit-any
  const playgroundInstanceRef = useRef<any>(null);
  const playgroundInitRef = useRef(false);
  const intentionalCloseRef = useRef(false);
  const initTimerRef = useRef<number | undefined>(undefined);

  // WebSocket connection
  useEffect(() => {
    if (typeof globalThis.window === "undefined") return;
    if (!roomId) return;

    console.log("[PlaygroundRoom] Effect running", { roomId, retryCount });

    // Singleton Guard check
    const globalKey = `__pg_room_${roomId}`;
    // deno-lint-ignore no-explicit-any
    const existingInfo = (globalThis as any)[globalKey];
    if (
      existingInfo && existingInfo.connected &&
      Date.now() - existingInfo.timestamp < 1000
    ) {
      console.warn(
        "[PlaygroundRoom] Duplicate connection attempt detected - aborting",
      );
      setIsDuplicate(true);
      return;
    }

    // Mark as connecting
    // deno-lint-ignore no-explicit-any
    (globalThis as any)[globalKey] = { connected: true, timestamp: Date.now() };

    // Cleanup previous
    if (wsRef.current) {
      console.log("[PlaygroundRoom] Closing previous socket");
      wsRef.current.close();
    }

    const protocol = globalThis.location.protocol === "https:" ? "wss:" : "ws:";
    const wsUrl = new URL(
      `${protocol}//${globalThis.location.host}/ws/playground`,
    );
    wsUrl.searchParams.set("room", roomId);
    if (user?.id) {
      wsUrl.searchParams.set("playerId", user.id);
    } else {
      // Try to reuse guest ID
      const storedGuestId = sessionStorage.getItem("pg:guestId");
      if (storedGuestId) wsUrl.searchParams.set("playerId", storedGuestId);
    }
    if (user?.isAnonymous) wsUrl.searchParams.set("anon", "true");

    console.log("[PlaygroundRoom] Connecting to", wsUrl.toString());
    const ws = new WebSocket(wsUrl.toString());
    wsRef.current = ws;

    ws.onopen = () => {
      console.log("[PlaygroundRoom] Socket Open");
      setConnected(true);
      setIsDuplicate(false);
    };

    ws.onclose = (e) => {
      console.log("[PlaygroundRoom] Socket Closed", e.code, e.reason);
      // deno-lint-ignore no-explicit-any
      if ((globalThis as any)[globalKey]) {
        (globalThis as any)[globalKey].connected = false;
      }

      setConnected(false);
      setInitData(null); // Reset init data on disconnect
      // Retry logic
      setTimeout(() => setRetryCount((c) => c + 1), 2000);
    };

    ws.onerror = (e) => {
      console.error("[PlaygroundRoom] WS Error", e);
    };

    const handleMessage = (event: MessageEvent) => {
      try {
        const msg = JSON.parse(event.data);

        if (msg.type === "ROOM_NOT_FOUND") {
          setRoomNotFound({
            found: false,
            isReserved: msg.isReserved,
            isOwner: msg.isOwner,
          });
        } else if (msg.type === "INIT") {
          setRoomNotFound(false);
          setIsHost(msg.isHost || false);
          setIsPublic(msg.isPublic || false);
          setCurrentExperiment(msg.experiment || "");
          if (msg.chunkDepth) setChunkDepth(msg.chunkDepth);
          if (msg.chunkSize) setChunkSize(msg.chunkSize);
          if (msg.showGrid !== undefined) setShowGrid(msg.showGrid);
          if (msg.smartRender !== undefined) setSmartRender(msg.smartRender);
          if (msg.meshRender !== undefined) setMeshRender(msg.meshRender);

          // Save init data and trigger initialization via useEffect
          setInitData(msg);
        } else if (msg.type === "EXPERIMENT_SWITCHED") {
          setCurrentExperiment(msg.experiment);
        }

        // Forward message event for other listeners if needed
        document.dispatchEvent(
          new CustomEvent("pg:ws:message", { detail: msg }),
        );
      } catch (e) {
        console.error("Failed to parse WS message", e);
      }
    };

    ws.addEventListener("message", handleMessage);

    return () => {
      console.log("[PlaygroundRoom] Cleanup Effect");
      // deno-lint-ignore no-explicit-any
      if ((globalThis as any)[globalKey]) {
        (globalThis as any)[globalKey].connected = false;
      }

      // Cleanup WebSocket and Playground Instance
      ws.onclose = null;
      ws.removeEventListener("message", handleMessage);
      ws.close();

      // Destroy playground instance
      if (playgroundInstanceRef.current) {
        playgroundInstanceRef.current.destroy();
        playgroundInstanceRef.current = null;
      }
      playgroundInitRef.current = false;

      // Prevent UI flash if just changing dependencies?
      // Actually if we disconnect socket, we SHOULD show loading.
      setConnected(false);

      // Cancel pending init
      if (initTimerRef.current) {
        clearTimeout(initTimerRef.current);
      }
    };
  }, [roomId, retryCount, user?.id]);

  // Sync state from playground.ts events
  useEffect(() => {
    const handleHostUpdate = (e: CustomEvent) => {
      setIsHost(!!e.detail);
    };

    const handleStateUpdate = (e: CustomEvent) => {
      const d = e.detail;
      if (!d) return;
      if (typeof d.isPublic === "boolean") setIsPublic(d.isPublic);
      if (d.chunkDepth !== undefined) setChunkDepth(d.chunkDepth);
      if (d.chunkSize !== undefined) setChunkSize(d.chunkSize);
      if (d.showGrid !== undefined) setShowGrid(d.showGrid);
      if (d.smartRender !== undefined) setSmartRender(d.smartRender);
      if (d.meshRender !== undefined) setMeshRender(d.meshRender);
      if (d.experiment !== undefined) setCurrentExperiment(d.experiment);
    };

    document.addEventListener(
      "pg:host:update",
      handleHostUpdate as EventListener,
    );
    document.addEventListener(
      "pg:state:update",
      handleStateUpdate as EventListener,
    );

    return () => {
      document.removeEventListener(
        "pg:host:update",
        handleHostUpdate as EventListener,
      );
      document.removeEventListener(
        "pg:state:update",
        handleStateUpdate as EventListener,
      );
    };
  }, []);

  // Lifecycle Management: Destroy game when UI invalidates (Room Not Found / Disconnected)
  useEffect(() => {
    if (roomNotFound || !connected) {
      if (playgroundInstanceRef.current) {
        console.log("[PlaygroundRoom] UI Invalidated -> Destroying Instance");
        playgroundInstanceRef.current.destroy();
        playgroundInstanceRef.current = null;
        playgroundInitRef.current = false;
      }
    }
  }, [roomNotFound, connected]);

  // Initialize Playground when container and data are ready
  useEffect(() => {
    if (
      !playgroundInitRef.current && containerRef.current && initData &&
      wsRef.current && connected && !roomNotFound
    ) {
      console.log("[PlaygroundRoom] Starting Game Instance");
      playgroundInitRef.current = true;
      initPlayground(wsRef.current, initData);
    }
  }, [initData, roomNotFound, connected]); // Dependencies trigger check

  // Initialize Three.js playground
  // deno-lint-ignore no-explicit-any
  const initPlayground = (ws: WebSocket, initData: any) => {
    // Double check to prevent race conditions
    if (playgroundInstanceRef.current) {
      console.warn("[PlaygroundRoom] Instance already exists, skipping init");
      return;
    }

    // Store socket for playground.ts to use
    // deno-lint-ignore no-explicit-any
    (globalThis as any).__playgroundSocket = ws;
    // deno-lint-ignore no-explicit-any
    (globalThis as any).__playgroundInitData = initData;

    // Persist guest ID if not logged in
    if (!user?.id && initData.yourId) {
      try {
        sessionStorage.setItem("pg:guestId", initData.yourId);
      } catch (e) {
        console.error("Failed to save guest ID", e);
      }
    }

    const check = () => {
      // deno-lint-ignore no-explicit-any
      if ((globalThis as any).initPlayground && containerRef.current) {
        // Check again before launch
        if (playgroundInstanceRef.current) return;

        // deno-lint-ignore no-explicit-any
        console.log("[PlaygroundRoom] Launching initPlayground");
        playgroundInstanceRef.current = (globalThis as any).initPlayground(
          containerRef.current,
          roomId,
        );
      } else {
        initTimerRef.current = setTimeout(check, 50);
      }
    };
    check();
  };

  const handleCreate = async () => {
    try {
      const res = await fetch("/api/playground/rooms", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ customId: roomId, isPublic: true }),
      });
      if (res.ok) {
        setRoomNotFound(false);
        setRetryCount((c) => c + 1); // Trigger reconnect
      }
    } catch (err) {
      console.error("Failed to create room", err);
    }
  };

  if (isDuplicate) return null; // Hide duplicate instances

  // Room not found - show create UI
  if (roomNotFound) {
    const canCreate = user && !user.isAnonymous;
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-base-200 p-4 space-y-6">
        <div className="text-center space-y-2">
          <h2 className="text-4xl font-black text-primary tracking-tighter uppercase">
            Room Not Found
          </h2>
          <p className="opacity-60 font-bold italic">
            The room code "{roomId}" doesn't exist yet.
          </p>
        </div>
        <div className="card w-full max-w-sm bg-base-100 shadow-2xl overflow-hidden">
          <div className="card-body items-center text-center space-y-4">
            {canCreate
              ? (
                <>
                  <div className="text-xs uppercase font-black opacity-30 tracking-widest">
                    Wanna start it?
                  </div>
                  <button
                    type="button"
                    className="btn btn-primary btn-lg w-full font-black uppercase text-xl h-20 shadow-xl"
                    onClick={handleCreate}
                  >
                    Create Room {roomId}
                  </button>
                </>
              )
              : (
                <>
                  <div className="text-xs uppercase font-black opacity-30 tracking-widest">
                    Login Required
                  </div>
                  <div className="p-4 bg-base-200 rounded-lg w-full">
                    <p className="text-sm opacity-70">
                      You need to be logged in to create rooms.
                    </p>
                  </div>
                  <a
                    href="/login"
                    className="btn btn-primary w-full font-black uppercase"
                  >
                    Login
                  </a>
                </>
              )}
            <a
              href="/playground"
              className="btn btn-ghost btn-sm opacity-50 font-black"
            >
              Back to Lobby
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Loading / connecting
  if (!connected) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-base-300">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    );
  }

  // Room exists - render game container and UI
  return (
    <div className="fixed inset-0 w-full h-full overflow-hidden">
      <Menu
        isHost={isHost}
        isPublic={isPublic}
        currentExperiment={currentExperiment}
        chunkDepth={chunkDepth}
        chunkSize={chunkSize}
        showGrid={showGrid}
        smartRender={smartRender}
        meshRender={meshRender}
      />
      <HUD roomId={roomId} />
      <div ref={containerRef} className="w-full h-full"></div>
    </div>
  );
}
